/**
 * YMMEntryRow Component
 * 
 * A single row for entering YMM (Year/Make/Model) restriction data.
 * Provides inputs for Make, Model, YearFrom, and YearTo with validation.
 * 
 * Requirements: 2.1, 2.5
 */

import React from 'react';
import type { YMMRestriction } from '../types/product';
import { YMM_YEAR_MIN, YMM_YEAR_MAX, isValidYMMYear } from '../utils/ymm';

interface YMMEntryRowProps {
  restriction: YMMRestriction;
  index: number;
  onChange: (index: number, field: keyof YMMRestriction, value: string | number) => void;
  onRemove: (index: number) => void;
  retain?: boolean;
  onRetainToggle?: () => void;
}

export const YMMEntryRow: React.FC<YMMEntryRowProps> = ({
  restriction,
  index,
  onChange,
  onRemove,
  retain = false,
  onRetainToggle,
}) => {
  const handleTextChange = (field: 'make' | 'model') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange(index, field, e.target.value);
  };

  const handleYearChange = (field: 'yearFrom' | 'yearTo') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
    onChange(index, field, isNaN(value) ? 0 : value);
  };

  const yearFromValid = isValidYMMYear(restriction.yearFrom);
  const yearToValid = isValidYMMYear(restriction.yearTo);
  const yearRangeValid = restriction.yearFrom === 0 || restriction.yearTo === 0 || 
    restriction.yearFrom <= restriction.yearTo;

  return (
    <div className="ymm-entry-row" data-testid={`ymm-row-${index}`}>
      <div className="ymm-field">
        <label htmlFor={`ymm-make-${index}`}>Make</label>
        <input
          type="text"
          id={`ymm-make-${index}`}
          value={restriction.make}
          onChange={handleTextChange('make')}
          placeholder="e.g., Ford"
        />
      </div>
      
      <div className="ymm-field">
        <label htmlFor={`ymm-model-${index}`}>Model</label>
        <input
          type="text"
          id={`ymm-model-${index}`}
          value={restriction.model}
          onChange={handleTextChange('model')}
          placeholder="e.g., F-150"
        />
      </div>
      
      <div className="ymm-field">
        <label htmlFor={`ymm-yearfrom-${index}`}>Year From</label>
        <input
          type="number"
          id={`ymm-yearfrom-${index}`}
          value={restriction.yearFrom || ''}
          onChange={handleYearChange('yearFrom')}
          min={YMM_YEAR_MIN}
          max={YMM_YEAR_MAX}
          placeholder={String(YMM_YEAR_MIN)}
          className={!yearFromValid || !yearRangeValid ? 'invalid' : ''}
        />
        {!yearFromValid && restriction.yearFrom !== 0 && (
          <span className="error-text">Year must be {YMM_YEAR_MIN}-{YMM_YEAR_MAX}</span>
        )}
      </div>
      
      <div className="ymm-field">
        <label htmlFor={`ymm-yearto-${index}`}>Year To</label>
        <input
          type="number"
          id={`ymm-yearto-${index}`}
          value={restriction.yearTo || ''}
          onChange={handleYearChange('yearTo')}
          min={YMM_YEAR_MIN}
          max={YMM_YEAR_MAX}
          placeholder={String(YMM_YEAR_MAX)}
          className={!yearToValid || !yearRangeValid ? 'invalid' : ''}
        />
        {!yearToValid && restriction.yearTo !== 0 && (
          <span className="error-text">Year must be {YMM_YEAR_MIN}-{YMM_YEAR_MAX}</span>
        )}
        {!yearRangeValid && (
          <span className="error-text">Year From must be ≤ Year To</span>
        )}
      </div>
      
      <label className="ymm-retain-checkbox">
        <input
          type="checkbox"
          checked={retain}
          onChange={onRetainToggle}
          aria-label={`Keep fitment ${index + 1} for next entry`}
        />
        <span>Keep</span>
      </label>
      
      <button
        type="button"
        className="ymm-remove-btn"
        onClick={() => onRemove(index)}
        aria-label={`Remove YMM entry ${index + 1}`}
      >
        ✕
      </button>
    </div>
  );
};
